/* import __COLOCATED_TEMPLATE__ from './sales-performance.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import { HEIGHT_TYPES, WIDTH_TYPES } from 'embercom/models/reporting/custom/chart';
import { VISUALIZATION_TYPES } from 'embercom/models/reporting/custom/visualization-options';
import { filterableProperties as defaultFilterableProperties } from 'embercom/lib/reporting/custom/filter-helpers';
import type ConversationAttributeDescriptor from 'embercom/models/conversation-attributes/descriptor';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { get } from 'embercom/lib/ajax';
import { SdrQualificationFunnelConfig } from 'embercom/components/reporting/bespoke/sdr-performance/qualification-funnel/series-config';

const DEFAULT_CHART_DEFINITION = {
  id: null,
  title: null,
  visualization_type: null,
  metric_property: null,
  metric_function: null,
  stacked: false,
  view_by: null,
  view_by_time_interval: null,
  view_by_display_limit: 10,
  view_by_display_other: false,
  width_type: null,
  height_type: null,
};

type RoutingOutcome = {
  identifier: string;
  title: string;
  workflow: any;
  fin_instruction_text: string;
  predicates: any[];
  required_memory_fields: any[];
  optional_memory_fields: any[];
};

type SdrConfig = {
  app_id: number;
  routing_outcomes: RoutingOutcome[];
  general_instructions: any;
  integrations: any;
};

interface Signature {
  Args: {
    conversationAttributeDescriptors: ConversationAttributeDescriptor[];
  };
  Blocks: {
    default: [];
  };
  Element: null;
}

export default class SalesPerformance extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare aiAgentSetupService: AiAgentSetupService;

  @tracked finSdrConfig: SdrConfig | null = null;

  constructor(owner: any, args: Signature['Args']) {
    super(owner, args);
    taskFor(this.loadFinSdrConfig).perform();
  }

  @task({ restartable: true })
  *loadFinSdrConfig(): TaskGenerator<void> {
    try {
      let appId = this.appService.app.id;
      let response = (yield get(`/teammate_app/fin_sdr_config?app_id=${appId}`)) as SdrConfig;
      this.finSdrConfig = response;
    } catch (error) {
      console.error('Failed to load SDR config:', error);
      this.finSdrConfig = null;
    }
  }

  get serializedReport() {
    return {
      id: 'fin-sdr-performance',
      title: null,
      description: null,
      charts: [],
      filters: {},
      date_range: {
        selection: 'past_4_weeks',
      },
    };
  }

  get filterableProperties() {
    return defaultFilterableProperties().concat(
      'teammate',
      'team',
      'conversation.replied.workflow_ids',
    );
  }

  get salesPerformanceOverviewCharts() {
    return [
      {
        ...DEFAULT_CHART_DEFINITION,
        id: 'sdr_conversation_volume',
        title: this.intl.t('ai-agent.analyze.sales-performance.conversation-volume'),
        visualization_type: VISUALIZATION_TYPES.COUNTER,
        visualization_options: {
          show_time_comparison: true,
        },
        width_type: WIDTH_TYPES.TWO_COLUMN,
        height_type: HEIGHT_TYPES.X_SMALL,
        chart_series: [
          {
            metric_id: 'conversation.fin_sdr.participated.count',
            show_data_labels: true,
          },
        ],
      },
      {
        ...DEFAULT_CHART_DEFINITION,
        id: 'sdr_contact_capture_rate',
        title: this.intl.t('ai-agent.analyze.sales-performance.contact-capture-rate'),
        visualization_type: VISUALIZATION_TYPES.COUNTER,
        visualization_options: {
          show_time_comparison: true,
        },
        width_type: WIDTH_TYPES.TWO_COLUMN,
        height_type: HEIGHT_TYPES.X_SMALL,
        chart_series: [
          {
            metric_id: 'conversation.fin_sdr.contact_capture_rate',
            show_data_labels: true,
          },
        ],
      },
      {
        ...DEFAULT_CHART_DEFINITION,
        id: 'sdr_completion_rate',
        title: this.intl.t('ai-agent.analyze.sales-performance.completion-rate'),
        visualization_type: VISUALIZATION_TYPES.COUNTER,
        visualization_options: {
          show_time_comparison: true,
        },
        width_type: WIDTH_TYPES.TWO_COLUMN,
        height_type: HEIGHT_TYPES.X_SMALL,
        chart_series: [
          {
            metric_id: 'conversation.fin_sdr.participation_rate',
            show_data_labels: true,
          },
        ],
      },
    ];
  }

  get routingOutcomeCharts() {
    let outcomes = this.finSdrConfig?.routing_outcomes || [];

    return outcomes.map((outcome) => ({
      ...DEFAULT_CHART_DEFINITION,
      id: `sdr_routing_outcome_${outcome.identifier}`,
      title: outcome.title,
      visualization_type: VISUALIZATION_TYPES.COUNTER,
      visualization_options: {
        show_time_comparison: true,
      },
      width_type: WIDTH_TYPES.TWO_COLUMN,
      height_type: HEIGHT_TYPES.X_SMALL,
      chart_series: [
        {
          metric_id: 'conversation.fin_sdr.participated.count',
          show_data_labels: true,
          filters: {
            type: 'and',
            filters: [
              {
                type: 'category',
                data: {
                  attribute: 'conversation.fin_sdr.routing_outcome_title',
                  property: 'fin_sdr.routing_outcome.identifier',
                  values: [outcome.identifier],
                },
              },
            ],
          },
        },
      ],
    }));
  }

  get volumeOverTimeChart() {
    let outcomes = this.finSdrConfig?.routing_outcomes || [];
    if (outcomes.length === 0) {
      return null;
    }

    return {
      ...DEFAULT_CHART_DEFINITION,
      id: 'sdr_volume_over_time',
      title: this.intl.t('ai-agent.analyze.sales-performance.volume-over-time'),
      visualization_type: VISUALIZATION_TYPES.LINE,
      width_type: WIDTH_TYPES.FULL,
      height_type: HEIGHT_TYPES.TALL,
      view_by: 'time',
      chart_series: outcomes.map((outcome) => ({
        metric_id: 'conversation.fin_sdr.participated.count',
        label: outcome.title,
        filters: {
          type: 'and',
          filters: [
            {
              type: 'category',
              data: {
                attribute: 'conversation.fin_sdr.routing_outcome_title',
                property: 'fin_sdr.routing_outcome.identifier',
                values: [outcome.identifier],
              },
            },
          ],
        },
      })),
    };
  }

  get qualificationFunnelChart() {
    let routingOutcomes = this.finSdrConfig?.routing_outcomes || [];

    return {
      ...DEFAULT_CHART_DEFINITION,
      id: 'sdr_qualification_funnel',
      title: this.intl.t('ai-agent.analyze.sales-performance.qualification-funnel'),
      chart_series: SdrQualificationFunnelConfig.getChartSeriesWithLabels(
        this.intl,
        routingOutcomes,
      ),
      width_type: WIDTH_TYPES.FULL,
      height_type: HEIGHT_TYPES.X_TALL,
      visualization_type: VISUALIZATION_TYPES.BESPOKE,
      component_name: 'sdr-performance/qualification-funnel',
    };
  }

  get hasFinSdrLive() {
    return true;
  }

  get emptyState() {
    if (this.appService.app.canUseFeature('insights-override-fin-live-empty-states')) {
      return null;
    }

    if (!this.hasFinSdrLive) {
      return 'fin-not-live';
    }

    return null;
  }

  get shouldShowReportFilters() {
    return this.hasFinSdrLive;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAutomation::Analyze::SalesPerformance': typeof SalesPerformance;
    'ai-automation/analyze/sales-performance': typeof SalesPerformance;
  }
}
